import { BaseColDefs } from '@/list/column-defs/base-col-defs';
import { EditableDropdownCell } from '@/list/components/cells/editable-dropdown-cell';
import { InFlightValue } from '@/list/components/cells/in-flight-value';
import { dayjs } from '@/utils/dayjs';
import { caseInsensitiveComparator } from '@/utils/string-utils';
import { complaintTypeLabelFactory } from '../constants';
import {
  COMPLAINTS_COL_IDS,
  COMPLAINT_FIELDS,
  COMPLAINT_SOURCES,
} from './complaints-constants';
import { formatComplaintResolution } from './utils';

// function - returns string values that correspond to in-flight value objects, used when filtering
export const getComplaintsStatusFilterWithInFlightValues = (
  allStatusDescriptions
) => {
  const values = Object.keys(allStatusDescriptions);
  const inFlightValues = values.map((v) => new InFlightValue(v));
  return [...values, ...inFlightValues];
};

export const getComplaintsInFlightValues = (allStatusDescriptions) => {
  const values = Object.keys(allStatusDescriptions);
  return values.map((v) => new InFlightValue(v));
};

export class ComplaintsColDefs extends BaseColDefs {
  // eslint-disable-next-line class-methods-use-this
  onStatusColumnChanged = () => {};

  /*
   * Part of useStatus hook - This field stores all the status descriptions available for complaints.
   */
  allStatusDescriptions = null;

  /*
   * Part of useStatus hook - This field stores the function that returns the label for a given status.
   */
  complaintStatusLabelFactory = null;

  /**
   * Value passed back from `EditableDropdownCell` that contains the newly-picked
   * value and the id of the row whose field is being changed
   * @typedef {Object} EditableCellChangeEvent
   * @property {string} rowId Id of the row that contains the cell that is being changed
   * @property {any} cellValue The new value of the changed cell
   * @property {ColDef} colDef ag-grid column definition corresponding to the cell being changed
   */
  /**
   *
   * @param {(EditableCellChangeEvent) => void} onStatusColumnChanged Handler for changing status
   * dropdown in a complaints row
   */
  constructor(onStatusColumnChanged, options) {
    super();
    this.onStatusColumnChanged = onStatusColumnChanged;
    this.allStatusDescriptions = options.allStatusDescriptions;
    this.complaintStatusLabelFactory = options.complaintStatusLabelFactory;
  }

  dateTimeRaisedColumn = this.dateColumn({
    headerName: 'Date/Time Raised',
    field: 'submissionDate',
    colId: 'submissionDate',
    width: 160,
    valueFormatter: ({ value }) => dayjs(value).format('YYYY-MM-DD LT'),
    sort: 'desc',
  });

  complaintIdColumn = this.numberColumn({
    headerName: 'Complaint #',
    field: 'complaintId',
    colId: 'complaintId',
    autoHeight: true,
    width: 130,
  });

  getStatusColumn = () =>
    this.setColumn({
      headerName: 'Status',
      field: COMPLAINT_FIELDS.STATUS,
      colId: COMPLAINTS_COL_IDS.status,
      valueFormatter: ({ value }) => this.complaintStatusLabelFactory(value),

      // A status field values is either a string status or an instance of InFlightValue
      // that contains a status. Invoked on a string, the toString() method would return the
      // same string, and InFlightValue would have its own override for toString() that returns
      // a unique string based on status.
      keyCreator: ({ value }) => value?.toString(),

      // The above keyCreator allows the grid to support including in-flight values into
      // the set of filterable values.
      filterParams: {
        // If we do not include the InFlight values in this list, ag-grid won't be able
        // to apply in-flight values to the filter in order to make in-flight values visible
        // when filtering
        // TODO: figure out if this is a bug fixed in a future ag-grid version
        values: getComplaintsStatusFilterWithInFlightValues(
          this.allStatusDescriptions
        ),
      },
      // Do not set autoHeight, as it would make it harder to keep
      // the dropdown full-width.
      cellRendererFramework: EditableDropdownCell,
      cellRendererParams: {
        onChange: (...args) => this.onStatusColumnChanged(...args),
        dropdownOptions: Object.keys(this.allStatusDescriptions).map(
          (status) => ({
            label: this.complaintStatusLabelFactory(status),
            value: status,
          })
        ),
      },
    });

  complaintSourceColumn = this.setColumn({
    headerName: 'Source',
    field: 'source',
    keyCreator: ({ value }) => COMPLAINT_SOURCES[value],
    valueFormatter: ({ value }) => COMPLAINT_SOURCES[value],
  });

  complaintTypeColumn = this.setColumn({
    headerName: 'Complaint Type',
    field: 'complaintTypes',
    colId: COMPLAINTS_COL_IDS.complaintTypes,
    wrapText: true,
    autoHeight: true,
    cellStyle: { wordBreak: 'normal', overflowWrap: 'anywhere' },
    valueFormatter: ({ value }) =>
      value?.map(complaintTypeLabelFactory)?.join(', '),
  });

  propertyAddressColumn = this.textColumn({
    headerName: 'Supplied Address',
    field: 'streetAddress',
    colId: 'streetAddress',
    autoHeight: true,
    cellStyle: { textOverflow: 'ellipsis', display: 'block' },
    width: 280,
  });

  complainDescriptionColumn = this.textColumn({
    headerName: 'Complaint Description',
    field: 'description',
    colId: 'description',
    wrapText: true,
    cellStyle: { wordBreak: 'normal', overflowWrap: 'anywhere' },
    autoHeight: true,
    width: 350,
    comparator: caseInsensitiveComparator,
    useValueFormatterForExport: false,
    valueFormatter: ({ value }) =>
      value.length > 50 ? value.slice(0, 50) + '...' : value,
  });

  numAttachmentsColumn = this.numberColumn({
    headerName: 'Attachments',
    field: 'files',
    colId: 'numAttachments',
    valueFormatter: ({ value }) => value.length,
    width: 200,
  });

  firstComplaintColumn = this.textColumn({
    headerName: 'First complaint?',
    field: 'hasPriorComplaint',
    colId: 'hasPriorComplaint',
    valueFormatter: ({ value }) => (value ? 'No' : 'Yes'),
    autoHeight: true,
    width: 130,
  });

  priorComplaintColumn = this.textColumn({
    headerName: 'Prior Complaint ID',
    field: 'priorComplaintId',
    colId: 'priorComplaintId',
    autoHeight: true,
  });

  complainantNameColumn = this.textColumn({
    headerName: 'Complainant Name',
    field: 'contactFirstName',
    colId: 'contactFirstName',
    autoHeight: true,
  });

  complainantPhone = this.textColumn({
    headerName: 'Complainant Phone',
    field: 'contactPhone',
    colId: 'contactPhone',
    autoHeight: true,
  });

  complainantEmail = this.textColumn({
    headerName: 'Complainant Email',
    field: 'contactEmail',
    colId: 'contactEmail',
    autoHeight: true,
  });

  complaintResolutionColumn = this.textColumn({
    headerName: 'Complaint Resolution',
    field: 'callCentreResolution',
    colId: 'callCentreResolution',
    wrapText: true,
    cellStyle: {
      wordBreak: 'normal',
      overflowWrap: 'anywhere',
      whiteSpace: 'pre',
    },
    autoHeight: true,
    width: 600,
    comparator: caseInsensitiveComparator,
    valueFormatter: ({ value }) => formatComplaintResolution(value),
  });

  defaultColDef = {
    resizable: true,
    sortable: true,
    rowDrag: false,
    suppressMovable: true, // prevents column dragging
    cellStyle: {
      display: 'flex',
      alignItems: 'flex-start',
      overflow: 'hidden',
    },
  };

  generateAgGridColDefs() {
    return [
      this.dateTimeRaisedColumn,
      this.complaintIdColumn,
      this.getStatusColumn(),
      this.complaintSourceColumn,
      this.complaintTypeColumn,
      this.propertyAddressColumn,
      this.complainDescriptionColumn,
      this.numAttachmentsColumn,
      this.firstComplaintColumn,
      this.priorComplaintColumn,
      this.complainantNameColumn,
      this.complainantPhone,
      this.complainantEmail,
      this.complaintResolutionColumn,
    ];
  }

  generateSummarizedAgGridColDefs() {
    return [
      this.dateTimeRaisedColumn,
      this.complaintIdColumn,
      this.getStatusColumn(),
      this.complaintTypeColumn,
      this.complainantNameColumn,
      this.complainantPhone,
      this.complainantEmail,
    ];
  }

  getDefaultVisibleColumns() {
    return this.generateAgGridColDefs()
      .filter((colDef) => !colDef.hide)
      .map(({ colId }) => colId);
  }
}
